// Lecteur de pièce dans la plateforme — le CV s'ouvre par-dessus le
// panneau candidat au lieu d'un nouvel onglet. PDF et images : rendu
// natif du navigateur ; Word (.docx) : converti en HTML côté client
// (docx-preview, chargé à la demande) ; lien Google Drive : son aperçu
// intégré (/preview). Le reste (.doc) : téléchargement.
import { useEffect, useRef, useState } from 'react';
import { RecModalHost } from './CandidateModals.js';

export interface ViewerDoc {
  title: string;
  url: string;
  mime: string | null; // null = lien externe (Drive…)
}

const DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** drive.google.com/file/d/<id>/… ou docs.google.com/document/d/<id>/… → URL d'aperçu intégrable. */
export function drivePreviewUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(/^https:\/\/(drive|docs)\.google\.com\/(file|document|presentation|spreadsheets)\/d\/([\w-]+)/);
  if (m) return `https://${m[1]}.google.com/${m[2]}/d/${m[3]}/preview`;
  const open = url.match(/^https:\/\/drive\.google\.com\/open\?id=([\w-]+)/);
  return open ? `https://drive.google.com/file/d/${open[1]}/preview` : null;
}

function DocxView({ url }: { url: string }) {
  const box = useRef<HTMLDivElement>(null);
  const [err, setErr] = useState(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const [res, lib] = await Promise.all([fetch(url, { credentials: 'same-origin' }), import('docx-preview')]);
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (off || !box.current) return;
        await lib.renderAsync(blob, box.current, undefined, { inWrapper: true, ignoreLastRenderedPageBreak: true, useBase64URL: true });
        if (!off) setReady(true);
      } catch { if (!off) setErr(true); }
    })();
    return () => { off = true; };
  }, [url]);
  if (err) return <div className="rec-viewer-msg">Aperçu impossible pour ce document Word — téléchargez-le.</div>;
  return (
    <>
      {!ready && <div className="rec-viewer-msg">Ouverture…</div>}
      <div className="rec-viewer-docx" ref={box} />
    </>
  );
}

export function FileViewer({ doc, dark, onClose }: { doc: ViewerDoc; dark: boolean; onClose: () => void }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', k, true);
    return () => window.removeEventListener('keydown', k, true);
  }, [onClose]);

  const drive = doc.mime === null ? drivePreviewUrl(doc.url) : null;
  let body: React.ReactNode;
  if (drive) body = <iframe className="rec-viewer-frame" src={drive} title={doc.title} allow="autoplay" />;
  else if (doc.mime === 'application/pdf') body = <iframe className="rec-viewer-frame" src={`${doc.url}#view=FitH`} title={doc.title} />;
  else if (doc.mime?.startsWith('image/')) body = <div className="rec-viewer-img"><img src={doc.url} alt={doc.title} /></div>;
  else if (doc.mime === DOCX) body = <DocxView url={doc.url} />;
  else body = <div className="rec-viewer-msg">Aperçu indisponible pour ce format — téléchargez le fichier.</div>;

  return (
    <RecModalHost dark={dark}>
      <div className="rec-modal-ov rec-viewer-ov" onClick={onClose}>
        <div className="rec-viewer" role="dialog" aria-label={doc.title} onClick={(e) => e.stopPropagation()} data-testid="rec-viewer">
          <div className="rec-viewer-h">
            <b title={doc.title}>{doc.title}</b>
            {doc.mime !== null
              ? <a className="rec-ghost sm" href={doc.url} download>Télécharger</a>
              : <a className="rec-ghost sm" href={doc.url} target="_blank" rel="noreferrer">Ouvrir dans Drive ↗</a>}
            <button className="rec-x" onClick={onClose} aria-label="Fermer">✕</button>
          </div>
          <div className="rec-viewer-b">{body}</div>
        </div>
      </div>
    </RecModalHost>
  );
}
